import Button from '@rio-cloud/rio-uikit/Button';
import type { ReactNode } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';

import type { OrderStatus } from '#domain/Order.ts';
import type { StopType } from '#domain/Stop.ts';
import type { StopStatus } from '#domain/StopStatus.ts';
import { isSameDay } from '#utils/isSameDay.ts';

import { StopAndOrderStatus } from './StopAndOrderStatus';

type StopInfoProps = {
    streetAddress: string;
    zipCode?: string;
    city: string;
    name: ReactNode;
    from: string;
    to: string;
    country?: string;
    canEdit?: boolean;
    onEdit?: () => void;
    stopStatus?: StopStatus;
    stopType?: StopType;
    orderStatus?: OrderStatus;
    showStatusInfo?: boolean;
    addressFirst?: boolean;
    metadata?: ReactNode;
};

type StopTimeWindowProps = {
    from: string;
    to: string;
};

export const StopTimeWindow = ({ from, to }: StopTimeWindowProps) => {
    const sameDay = isSameDay(from, to);
    const showToDate = from !== to && !sameDay;
    const showToTime = from !== to;

    return (
        <div className='display-flex align-items-center gap-10 ellipsis-1'>
            <div className='display-flex align-items-center gap-2'>
                <span className='rioglyph rioglyph-calendar text-color-darker' />
                <span>
                    <FormattedDate value={from} />
                </span>
            </div>
            <div className='display-flex align-items-center gap-2'>
                <span className='rioglyph rioglyph-time-alt text-color-darker' />
                <span>
                    <FormattedTime value={from} />
                </span>
            </div>
            {showToDate && (
                <>
                    <span className='margin-x-2'>—</span>
                    <div className='display-flex align-items-center gap-2'>
                        <span className='rioglyph rioglyph-calendar text-color-darker' />
                        <span>
                            <FormattedDate value={to} />
                        </span>
                    </div>
                </>
            )}
            {showToTime && (
                <>
                    {sameDay && <span className='margin-x-2'>—</span>}
                    <div className='display-flex align-items-center gap-2'>
                        <span className='rioglyph rioglyph-time-alt text-color-darker' />
                        <span>
                            <FormattedTime value={to} />
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

export const StopInfo = ({
    name,
    streetAddress,
    zipCode,
    city,
    from,
    to,
    country,
    canEdit = false,
    onEdit,
    stopStatus,
    stopType,
    orderStatus,
    showStatusInfo = false,
    addressFirst = false,
    metadata,
}: StopInfoProps) => {
    const isEditable = canEdit && Boolean(onEdit);
    const address = `${country ?? ''} - ${zipCode ?? ''} ${city}, ${streetAddress}`;
    const primaryText = addressFirst ? address : name;

    const LocationName = () => (
        <div className={`text-bold ${addressFirst ? 'text-size-14' : 'text-size-16'} line-height-18 ellipsis-1`}>
            {primaryText}
        </div>
    );

    return (
        <div className={`display-flex flex-column ${addressFirst ? 'gap-5' : 'gap-2 max-width-400'}`}>
            {isEditable ? (
                <Button
                    type='button'
                    bsStyle='unstyled'
                    bsSize={Button.XS}
                    variant='link-inline'
                    className='display-flex align-items-center gap-5 cursor-pointer padding-0 text-left'
                    onClick={onEdit}
                    iconName='rioglyph-pencil'
                    iconRight
                    noRippleEffect
                    style={{
                        alignSelf: 'flex-start',
                        backgroundColor: 'transparent',
                        color: 'inherit',
                        maxWidth: '100%',
                    }}
                >
                    <div className='text-bold text-size-16 line-height-18 ellipsis-1'>{primaryText}</div>
                </Button>
            ) : (
                <LocationName />
            )}

            <div className={addressFirst ? 'text-size-12 text-color-dark ellipsis-1' : 'text-medium ellipsis-1'}>
                {addressFirst ? name : address}
            </div>

            <div
                className={`display-flex align-items-center gap-10 ${addressFirst ? 'flex-wrap text-size-12 text-color-dark' : 'ellipsis-1'}`}
            >
                <StopTimeWindow from={from} to={to} />
                {metadata}
                {showStatusInfo && (
                    <StopAndOrderStatus stopStatus={stopStatus} stopType={stopType} orderStatus={orderStatus} />
                )}
            </div>
        </div>
    );
};
